import React, { useMemo } from "react";
import { IconEye } from "@tabler/icons-react";
import { Printable } from "@components";
import { PatientHeader } from "./components/PatientHeader";
import { useLazyEyeData } from "./hooks/useLazyEyeData";
import { LazyEyeOverallAssessmentCard } from "./components/LazyEyeOverallAssessmentCard";
import { LazyEyeWhatIsCard } from "./components/LazyEyeWhatIsCard";
import { LazyEyeVaCard } from "./components/LazyEyeVaCard";
import { LazyEyeEyeAlignmentCard } from "./components/LazyEyeEyeAlignmentCard";
import { LazyEyePhoriaCard } from "./components/LazyEyePhoriaCard";
import { LAZY_EYE_HEADER_COLOR } from "@constants/resultColors";
import { aggregateSeverity, OverallSeverity } from "./utils/overallStatus";
import { Grid, Paper, Text, Title, Box, LoadingOverlay } from "@mantine/core";

export interface LazyEyeRiskOverviewProps {
  coverTestUuid?: string;
  leftVaUuid?: string;
  rightVaUuid?: string;
}

const HEADER_LABEL: Record<OverallSeverity, string> = {
  attention: "High Risk",
  monitor: "Moderate Risk",
  normal: "Low Risk",
};

export default function LazyEyeRiskOverview({
  coverTestUuid,
  leftVaUuid,
  rightVaUuid,
}: LazyEyeRiskOverviewProps) {
  const {
    patientData,
    ageBand,
    coverTestData,
    vaTest1,
    vaTest2,
    vaLogMar1,
    vaLogMar2,
    vaStatusTokens,
    interocDiffLogMar,
    isPhoria,
    phoriaData,
    findings,
    combined,
    hasCoverTest,
    hasVaTest1,
    hasVaTest2,
    loading,
  } = useLazyEyeData(coverTestUuid, leftVaUuid, rightVaUuid);

  const headerSeverity = useMemo(
    () =>
      aggregateSeverity([
        combined?.level ?? null,
        findings.vaInteroc?.level ?? null,
        ...vaStatusTokens,
      ]),
    [combined, findings.vaInteroc, vaStatusTokens],
  );

  const headerOverallStatus = headerSeverity
    ? { label: HEADER_LABEL[headerSeverity], color: LAZY_EYE_HEADER_COLOR[headerSeverity] }
    : undefined;

  return (
    <Printable.Section>
      <Box>
        <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />

        <PatientHeader
          patientData={patientData}
          icon={<IconEye size={24} color="white" />}
          title="Lazy Eye Risk Assessment"
          subtitle="Amblyopia Screening Report"
          overallStatus={headerOverallStatus}
        />

        {combined && (
          <LazyEyeOverallAssessmentCard
            combined={combined}
            vaInterocDiffLogMar={interocDiffLogMar}
          />
        )}

        <LazyEyeWhatIsCard patientAge={patientData.age} />

        <Title order={3} mb="md">
          Test Results Breakdown
        </Title>

        <Grid gutter="md">
          {(hasVaTest1 || hasVaTest2) && (
            <Grid.Col span={{ base: 12 }}>
              <LazyEyeVaCard
                vaTest1={vaTest1}
                vaTest2={vaTest2}
                ageBand={ageBand}
                vaLogMar1={vaLogMar1}
                vaLogMar2={vaLogMar2}
                interocDiffLogMar={interocDiffLogMar}
              />
            </Grid.Col>
          )}

          {hasCoverTest && (
            <Grid.Col span={{ base: 12 }}>
              {isPhoria && phoriaData ? (
                <LazyEyePhoriaCard
                  phoriaData={phoriaData}
                  ageBand={ageBand}
                  riskLevel={findings.phoria?.level ?? "low"}
                />
              ) : (
                <LazyEyeEyeAlignmentCard
                  coverTestData={coverTestData}
                  riskLevel={findings.coverTest?.level ?? "low"}
                  ageBand={ageBand}
                />
              )}
            </Grid.Col>
          )}
        </Grid>

        <FooterPanel severity={headerSeverity} />
      </Box>
    </Printable.Section>
  );
}

const FOOTER_TITLE: Record<"null" | OverallSeverity, string> = {
  null: "Not Enough Data",
  attention: "Follow-Up Recommended",
  monitor: "Worth Monitoring",
  normal: "Regular Eye Exams Matter",
};

const FOOTER_TEXT: Record<"null" | OverallSeverity, string> = {
  null: "Complete the relevant tests to see the full overview.",
  attention:
    "Results suggest a closer look from your eye care provider would be worthwhile. Early evaluation makes a difference.",
  monitor: "Some results were outside the ideal range. Regular check-ups will help track them.",
  normal: "Routine eye tests catch issues early when they're easiest to treat.",
};

function FooterPanel({ severity }: { severity: OverallSeverity | null }) {
  const key = severity ?? "null";
  return (
    <Paper mt="sm" p="sm" withBorder radius="md" style={{ textAlign: "center" }}>
      <Title order={4} mb="xs">
        {FOOTER_TITLE[key]}
      </Title>
      <Text size="sm" c="dimmed">
        {FOOTER_TEXT[key]}
      </Text>
    </Paper>
  );
}
